import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, FileDown, Trophy, Eye, Download } from "lucide-react";
import Plate from "@/components/Plate";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { downloadXlsx, fmtDate, fmtBool, todayStamp } from "@/lib/csv";

const TVA_RATE = 0.20; // France - TVA standard 20%

// Excel-compatible export columns (numbers stay numeric, prefixed with TVA-derived figures)
function buildVoVendusColumns() {
  return [
    { key: "date_vente", label: "Date vente", format: fmtDate },
    { key: "date_depart", label: "Date départ", format: fmtDate },
    { key: "numero_vo", label: "N° VO" },
    { key: "plaque", label: "Plaque" },
    { key: "marque", label: "Marque" },
    { key: "modele", label: "Modèle" },
    { key: "annee", label: "Année", type: "number" },
    { key: "vin", label: "VIN" },
    { key: "couleur", label: "Couleur" },
    { key: "kilometrage", label: "Kilométrage", type: "number" },
    { key: "client_nom", label: "Client" },
    { key: "client_telephone", label: "Téléphone" },
    { key: "prix_vente", label: "Prix TTC", type: "number" },
    {
      key: "prix_vente",
      label: `Base HT (${(TVA_RATE * 100).toFixed(0)}%)`,
      type: "number",
      format: (v) => Number((Number(v || 0) / (1 + TVA_RATE)).toFixed(2)),
    },
    {
      key: "prix_vente",
      label: "TVA",
      type: "number",
      format: (v) => {
        const ttc = Number(v || 0);
        return Number((ttc - ttc / (1 + TVA_RATE)).toFixed(2));
      },
    },
    { key: "carte_grise_disponible", label: "Carte grise", format: fmtBool },
  ];
}

export default function VOVendus() {
  const { user } = useAuth();
  const isCommercial = user?.role === "commercial";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/ventes", { params: { statut: "paye" } });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = q.trim()
    ? items.filter((v) => {
        const t = q.trim().toLowerCase();
        return (
          (v.plaque || "").toLowerCase().includes(t.replace(/[-\s]/g, "")) ||
          (v.client_nom || "").toLowerCase().includes(t) ||
          (v.marque || "").toLowerCase().includes(t) ||
          (v.modele || "").toLowerCase().includes(t)
        );
      })
    : items;

  const totalCA = filtered.reduce((s, v) => s + Number(v.prix_vente || 0), 0);

  const exportXlsx = () => {
    if (filtered.length === 0) {
      toast.error("Aucun VO vendu à exporter");
      return;
    }
    downloadXlsx(`vo-vendus-${todayStamp()}`, buildVoVendusColumns(), filtered, "VO vendus");
    toast.success(`${filtered.length} vente(s) exportée(s) (Excel)`);
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-700 font-semibold flex items-center gap-2">
            <Trophy size={12} /> Archives · Soldés
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">VO vendus</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {filtered.length} véhicule(s) entièrement encaissé(s)
            {!isCommercial && <> · CA total <span className="font-display font-bold text-foreground">{money(totalCA)}</span></>}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="vovendus-search"
              type="search"
              name="vovendus-search"
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher…"
              className="pl-9 rounded-sm"
            />
          </div>
          {!isCommercial && (
            <Button
              type="button"
              onClick={exportXlsx}
              data-testid="vovendus-export-csv"
              variant="outline"
              className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
            >
              <Download size={14} className="mr-1" /> Excel
            </Button>
          )}
        </div>
      </header>


      <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="vovendus-table">
        <table className="w-full text-sm">
          <thead className="bg-emerald-50/50 text-left text-xs uppercase tracking-wider text-emerald-900">
            <tr>
              <th className="px-6 py-3">Actions</th>
              <th className="px-6 py-3">Date vente</th>
              <th className="px-6 py-3">Date départ</th>
              <th className="px-6 py-3">Plaque</th>
              <th className="px-6 py-3">Véhicule</th>
              <th className="px-6 py-3">Client</th>
              <th className="px-6 py-3">Téléphone</th>
              <th className="px-6 py-3 text-center">Carte grise</th>
              <th className="px-6 py-3 text-right">Prix encaissé</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="px-6 py-10 text-center text-muted-foreground">
                Aucun véhicule soldé pour l'instant. Les ventes apparaîtront ici une fois entièrement encaissées.
              </td></tr>
            )}
            {filtered.map((v) => (
              <tr key={v.id} className="border-t hover:bg-emerald-50/30 transition-colors">
                <td className="px-6 py-3 whitespace-nowrap">
                  <Link
                    to={`/ventes/${v.id}`}
                    data-testid={`vovendus-view-${v.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-klein/5 hover:bg-klein/10 text-klein border border-klein/20 rounded-sm font-semibold"
                  >
                    <Eye size={12} /> Détail
                  </Link>
                </td>
                <td className="px-6 py-3 whitespace-nowrap">{dateFR(v.date_vente)}</td>
                <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">
                  {v.date_depart ? dateFR(v.date_depart) : "—"}
                </td>
                <td className="px-6 py-3"><Plate value={v.plaque} small /></td>
                <td className="px-6 py-3 font-medium">{v.marque} {v.modele} {v.annee ? `(${v.annee})` : ""}</td>
                <td className="px-6 py-3">{v.client_nom}</td>
                <td className="px-6 py-3 whitespace-nowrap">
                  {v.client_telephone
                    ? <a href={`tel:${v.client_telephone}`} className="text-klein hover:underline font-mono text-xs">{v.client_telephone}</a>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-6 py-3 text-center">
                  {v.carte_grise_disponible ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                      OK
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider bg-red-50 text-destructive border border-red-200">
                      Manquante
                    </span>
                  )}
                </td>
                <td className="px-6 py-3 text-right font-mono font-semibold text-emerald-700">{money(v.prix_vente)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
